// Parent unified inbox — /parent/inbox.
//
// Full-page companion to the NotificationBell dropdown. Reuses the same
// `useNotificationCenter` hook and the same `describeNotification`
// helper so there is exactly ONE source of truth for notification copy,
// icons, and deep-links. Adds:
//   * a tappable group-filter row (All / Messages / Billing / Events / ...)
//   * a "Mark all read" affordance
//   * calm mobile-first card rows
//   * per-filter empty state
//
// No new backend. No new RPCs. Fetches up to 100 recent rows via the
// existing rpc_list_my_notifications.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Bell, BellRinging, ChatCircleText, FirstAid, Receipt,
  WarningCircle, CalendarCheck, Camera, Sparkle, Sun, Flag, At,
  CheckCircle, Gear,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import useNotificationCenter from "@/lib/hooks/useNotificationCenter";
import {
  describeNotification,
  relTime,
  groupNotifications,
  NOTIFICATION_GROUPS,
  GROUP_META,
  projectNotificationRows,
} from "@/lib/notificationLinks";
import { supabase, isSupabaseConfigured } from "@/lib/supabase";

const ICON_MAP = {
  Bell, BellRinging, ChatCircleText, FirstAid, Receipt,
  WarningCircle, CalendarCheck, Camera, Sparkle, Sun, Flag, At,
};

// Page pulls up to 100 — the RPC clamps at 100 server-side.
const PAGE_LIMIT = 100;

export default function ParentInbox() {
  const { profile, role } = useAuth();
  const profileId = profile?.id || null;
  const organizationId = profile?.organization_id || null;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const requestRef = useRef(0);
  const { markSeen, unread } = useNotificationCenter(
    profileId,
    role,
    organizationId,
  );

  const refresh = useCallback(async () => {
    const requestId = ++requestRef.current;
    if (!isSupabaseConfigured || !profileId || !organizationId) {
      setRows([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc("rpc_list_my_notifications", {
        p_limit: PAGE_LIMIT,
        p_organization_id: organizationId,
      });
      if (error) throw error;
      if (requestId !== requestRef.current) return;
      setRows(projectNotificationRows(data, { role }));
    } catch (err) {
      if (requestId === requestRef.current) {
        toast.error(err.message || "Could not load inbox");
      }
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  }, [organizationId, profileId, role]);

  useEffect(() => {
    requestRef.current += 1;
    setRows([]);
    setFilter("all");
    setLoading(true);
    refresh();
    // Clear the bell badge after a short beat — the parent landed on
    // the full inbox page so "seen" is honest.
    const t = setTimeout(() => { markSeen(); }, 400);
    return () => {
      requestRef.current += 1;
      clearTimeout(t);
    };
  }, [markSeen, refresh]);

  const grouped = useMemo(() => groupNotifications(rows), [rows]);
  const counts = useMemo(() => {
    const c = { all: rows.length };
    for (const g of NOTIFICATION_GROUPS) c[g.key] = (grouped[g.key] || []).length;
    return c;
  }, [grouped, rows.length]);

  // Active set — either all rows, or the selected group.
  const activeRows = filter === "all" ? rows : (grouped[filter] || []);

  const onMarkAllRead = async () => {
    await markSeen();
    // Optimistically reflect on the page too.
    setRows((prev) => prev.map((r) => ({ ...r, is_unread: false })));
    toast.success("Marked all as read");
  };

  return (
    <div data-testid="parent-inbox" className="space-y-5">
      <header>
        <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
          Inbox
        </p>
        <div className="mt-1 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="font-heading text-3xl font-semibold tracking-tight text-neutral-900 sm:text-4xl">
              Your updates
            </h1>
            <p className="mt-1 max-w-md text-sm text-neutral-600">
              Messages, billing, events, incidents — all in one place.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {rows.some((r) => r.is_unread) ? (
              <button
                type="button"
                data-testid="parent-inbox-mark-all-read"
                onClick={onMarkAllRead}
                className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-700 hover:bg-neutral-50"
              >
                <CheckCircle size={12} weight="duotone" />
                Mark all read
              </button>
            ) : null}
            <Link
              to="/parent/preferences"
              data-testid="parent-inbox-preferences-link"
              className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-600 hover:bg-neutral-50"
            >
              <Gear size={12} weight="duotone" />
              Preferences
            </Link>
          </div>
        </div>
      </header>

      {/* Filter chips — horizontally scrollable on narrow screens. */}
      <nav
        aria-label="Inbox filters"
        data-testid="parent-inbox-filters"
        className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1"
      >
        <FilterChip
          testId="parent-inbox-filter-all"
          active={filter === "all"}
          count={counts.all}
          onClick={() => setFilter("all")}
          label="All"
          icon="Bell"
        />
        {NOTIFICATION_GROUPS.filter((g) => counts[g.key] > 0).map((g) => (
          <FilterChip
            key={g.key}
            testId={`parent-inbox-filter-${g.key}`}
            active={filter === g.key}
            count={counts[g.key]}
            onClick={() => setFilter(g.key)}
            label={g.label}
            icon={GROUP_META[g.key]?.icon || "Bell"}
          />
        ))}
      </nav>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
        </div>
      ) : activeRows.length === 0 ? (
        <EmptyState filter={filter} totalUnread={unread} />
      ) : (
        <ul
          data-testid="parent-inbox-list"
          data-filter={filter}
          className="space-y-2"
        >
          {activeRows.map((row) => (
            <InboxRow key={row.id} row={row} role={role} />
          ))}
        </ul>
      )}
    </div>
  );
}

function FilterChip({ testId, active, count, onClick, label, icon }) {
  const Icon = ICON_MAP[icon] || Bell;
  return (
    <button
      type="button"
      data-testid={testId}
      data-active={active ? "true" : "false"}
      onClick={onClick}
      className={`inline-flex flex-shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-all ${
        active
          ? "border-primary bg-primary text-white shadow-sm"
          : "border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50"
      }`}
    >
      <Icon size={12} weight="duotone" />
      {label}
      <span
        className={`rounded-full px-1.5 py-0 text-[10px] font-bold ${
          active ? "bg-white/20 text-white" : "bg-neutral-100 text-neutral-600"
        }`}
      >
        {count}
      </span>
    </button>
  );
}

function InboxRow({ row, role }) {
  const navigate = useNavigate();
  const { icon, label, path, statusTone, isFailure, detail } = describeNotification(row, role);
  const Icon = ICON_MAP[icon] || Bell;
  const clickable = !!path;

  const inner = (
    <>
      {row.is_unread && (
        <span
          aria-hidden="true"
          className="absolute left-2 top-5 h-2 w-2 rounded-full bg-[#C6851E]"
        />
      )}
      <span
        className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl ${
          isFailure ? "bg-[#C6851E]/10 text-[#C6851E]" : "bg-primary/10 text-primary-hover"
        }`}
      >
        <Icon size={18} weight="duotone" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-2">
          <p className="truncate text-sm font-semibold text-neutral-900">{label}</p>
          {role === "admin" && (
            <span
              className={`inline-flex items-center rounded-full border px-1.5 py-0 text-[9px] font-semibold uppercase tracking-wide ${statusTone}`}
            >
              {row.status}
            </span>
          )}
        </div>
        <p className="mt-0.5 line-clamp-2 text-xs text-neutral-600">{detail || row.subject || "—"}</p>
        <p className="mt-1 text-[11px] text-neutral-400">{relTime(row.created_at)}</p>
      </div>
    </>
  );

  const handleClick = (e) => {
    if (!clickable) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) return;
    e.preventDefault();
    navigate(path);
  };

  const testBase = `parent-inbox-row-${row.id}`;
  const liClasses = `relative flex items-start gap-3 rounded-2xl border px-4 py-3 transition ${
    row.is_unread ? "border-primary/30 bg-primary/[0.03]" : "border-neutral-200 bg-white"
  }`;

  if (clickable) {
    return (
      <li
        data-testid={testBase}
        data-kind={row.kind}
        data-unread={row.is_unread ? "true" : "false"}
        data-link={path}
        className={liClasses}
      >
        <Link
          to={path}
          onClick={handleClick}
          aria-label={`${label} — ${row.subject || "notification"}`}
          className="flex min-w-0 flex-1 items-start gap-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
        >
          {inner}
        </Link>
      </li>
    );
  }
  return (
    <li
      data-testid={testBase}
      data-kind={row.kind}
      data-unread={row.is_unread ? "true" : "false"}
      data-link=""
      className={liClasses}
    >
      {inner}
    </li>
  );
}

function EmptyState({ filter, totalUnread }) {
  const label = filter === "all"
    ? "Nothing here yet"
    : `Nothing in ${filterLabel(filter)}`;
  return (
    <div
      data-testid="parent-inbox-empty"
      data-filter={filter}
      className="rounded-2xl border border-dashed border-neutral-200 bg-white px-6 py-12 text-center"
    >
      <Bell size={36} weight="duotone" className="mx-auto text-neutral-300" />
      <p className="mt-3 font-heading text-base font-semibold text-neutral-800">{label}</p>
      <p className="mx-auto mt-1 max-w-xs text-xs text-neutral-500">
        {filter === "all"
          ? "When your teacher posts an update, we issue an invoice, or a new event appears, it'll show up here."
          : totalUnread > 0
            ? "Try another tab — you have unread updates in a different category."
            : "You're all caught up in this category."}
      </p>
    </div>
  );
}

function filterLabel(key) {
  return NOTIFICATION_GROUPS.find((g) => g.key === key)?.label?.toLowerCase() || key;
}
